import { lazy, Suspense } from 'react';
import { PerfBoundary } from '@core/components/effects/perf-boundary';
import LinkPreviewShadcn from './link-preview.shadcn';
import type { ComponentProps } from 'react';

/**
 * Lazy-loaded motion variant. The chunk is only fetched when the `animations`
 * performance category is enabled, keeping the initial bundle lighter.
 */
const LinkPreviewMotion = lazy(
  () => import('./link-preview.motion'),
);

/**
 * Performance-aware LinkPreview.
 *
 * Switches between the motion-animated variant (lazy-loaded, spring-scale
 * entrance + `useSpring` translateX drift) and the shadcn-only fallback
 * (Radix HoverCard `data-[state]:animate-in/out`) based on the `animations`
 * perf category from {@link usePerformanceStore}.
 *
 * Uses `preserveState` because the component owns `cachedImageSrc` — a
 * canvas-derived data-URL computed during hover. Losing that state on a
 * perf-mode toggle would force an expensive re-fetch of the canvas screenshot.
 * `isImageLoading` and `dialogOpen` are also preserved for UX consistency.
 *
 * @param props - Forwarded verbatim to both variants.
 */
const LinkPreview = (props: ComponentProps<typeof LinkPreviewShadcn>) => {
  return (
    <PerfBoundary
      preserveState
      animated={
        <Suspense fallback={<LinkPreviewShadcn {...props} />}>
          <LinkPreviewMotion {...props} />
        </Suspense>
      }
      fallback={<LinkPreviewShadcn {...props} />}
    />
  );
};

export default LinkPreview;
